import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Pencil, KeyRound, Trash2, X, RefreshCw, Search } from "lucide-react";
import { apiFetch, apiPatch, apiPost, apiDelete } from "./api/client";
import { UI } from "./designSystem";
import { getEffectiveRoles } from "./roleSimulator";

// Papéis de usuário (User.roles no mosaico-api). Uma pessoa pode acumular
// mais de um (ex.: ACOLHIMENTO + FINANCEIRO, sem precisar ser ADMIN só pra
// ver o financeiro) -- ADMIN continua acima de tudo em qualquer área, mesmo
// sem estar listado explicitamente em cada gate.
// ACOLHIMENTO era chamado de PASTOR (renomeado no enum do banco -- ver
// migration 20260925120000_rename_pastor_role_to_acolhimento no
// mosaico-api); continua liberando a Agenda de cuidado e a sincronia com
// a tabela de "Acolhedor(a)" exatamente como antes, so o nome mudou.
const ROLE_OPTIONS = ["ADMIN", "ACOLHIMENTO", "SECRETARIA", "SECRETARIA_CONSULTA", "RECEPCAO", "FINANCEIRO", "EDITOR_CONTEUDO", "MEMBER", "VISITANTE"];
const ROLE_LABELS = {
  ADMIN: "Admin",
  ACOLHIMENTO: "Acolhimento",
  SECRETARIA: "Secretaria",
  // Somente-leitura, com dado reduzido (LGPD) -- ver childDirectorySelect/
  // memberDirectorySelect no mosaico-api: sem CPF, endereço, contato,
  // saúde ou anotações internas. Pensado pra voluntário/recepção que só
  // precisa confirmar cadastro e família.
  SECRETARIA_CONSULTA: "Secretaria (consulta)",
  // Voluntário de recepção presencial -- só cadastra visitante novo no culto
  // e busca duplicidade/família (requireVisitorReceptionAccess em
  // conecta.js). Sem acesso a lista/kanban/histórico/conversão de
  // visitantes -- escopo bem mais restrito que SECRETARIA_CONSULTA, que é
  // sobre membros/crianças.
  RECEPCAO: "Recepção",
  FINANCEIRO: "Financeiro",
  EDITOR_CONTEUDO: "Editor de conteúdo",
  // "Músico" deixou de ser um papel atribuível aqui (29-30/10/2026, pedido
  // do Rodrigo/Drigo): ser músico é uma atividade de membro, igual
  // Café/Mídia/etc., e já mora em Person.isMusician (ver ESCALA_TIPO_FLAG
  // em lib/person.js, mosaico-api) -- hasMusicoAccess/hasEscalaAccess la'
  // ja' tratam isso como fonte de verdade de verdade, com User.roles
  // contendo "MUSICO" servindo so' de atalho/fallback legado (checado
  // DEPOIS de ADMIN). MUSICO continua valido no enum do backend
  // (routes/users.js) -- so' nao aparece mais pra marcar/desmarcar por
  // aqui, pra nao duplicar o que a Person ja' resolve. Quem ja' tinha o
  // papel continua com ele intacto: toggleRole so' adiciona/remove o papel
  // que foi clicado, preservando qualquer outro papel que a pessoa ja'
  // tinha (inclusive MUSICO) -- removido so' da tabela, nunca das contas
  // existentes.
  // Desde 10/2026 o cadastro novo no site/app nao ganha mais MEMBER por
  // padrao (ver roles: ["VISITANTE"] em auth.js) -- MEMBER agora so marca
  // quem a secretaria de fato vinculou a um cadastro de membresia.
  // VISITANTE e' o papel padrao de quem so tem conta no app, sem virar
  // membro oficial (por enquanto so rotulo, sem restringir acesso).
  MEMBER: "Membro",
  VISITANTE: "Visitante"
};

// Voltar dessas telas de administração deve ir pro painel de admin (/admin),
// não pro site.
const ADMIN_PATH = "/admin";

const pageStyle = {
  minHeight: "100vh",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: UI.shell,
  color: UI.text,
  fontFamily: "'DM Sans', system-ui, sans-serif",
  padding: "28px 20px 60px"
};

const cardStyle = {
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.28)",
  borderRadius: 16,
  padding: 18,
  boxShadow: "0 14px 28px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.06)"
};

const backLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  color: "#9fb0cc",
  fontSize: 13,
  fontWeight: 700,
  textDecoration: "none",
  marginBottom: 18
};

const iconButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  borderRadius: 8,
  border: "1px solid rgba(148,163,184,0.28)",
  background: "rgba(255,255,255,0.03)",
  color: "#c7ced9",
  cursor: "pointer"
};

const modalBackdropStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(4,8,18,0.72)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  zIndex: 50
};

const modalCardStyle = {
  width: "100%",
  maxWidth: 420,
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.32)",
  borderRadius: 16,
  padding: 22,
  boxShadow: "0 24px 60px rgba(0,0,0,0.5)"
};

const modalCloseButtonStyle = {
  position: "absolute",
  top: 14,
  right: 14,
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  background: "rgba(255,255,255,0.06)",
  borderRadius: 8,
  color: "#9fb0cc",
  cursor: "pointer"
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid rgba(148,163,184,0.32)",
  background: "rgba(255,255,255,0.04)",
  color: "#e5e7eb",
  fontSize: 14,
  fontFamily: "inherit"
};

const fieldLabelStyle = {
  display: "block",
  margin: "0 0 6px",
  fontSize: 12,
  fontWeight: 800,
  color: "#9fb0cc"
};

const primaryButtonStyle = {
  minHeight: 40,
  padding: "0 18px",
  borderRadius: 9,
  border: "1px solid rgba(125,211,252,0.4)",
  background: "rgba(14,165,233,0.18)",
  color: "#BAE6FD",
  fontSize: 13,
  fontWeight: 900,
  cursor: "pointer"
};

const secondaryButtonStyle = {
  minHeight: 40,
  padding: "0 18px",
  borderRadius: 9,
  border: "1px solid rgba(148,163,184,0.32)",
  background: "transparent",
  color: "#c7ced9",
  fontSize: 13,
  fontWeight: 800,
  cursor: "pointer"
};

const dangerButtonStyle = {
  minHeight: 40,
  padding: "0 18px",
  borderRadius: 9,
  border: "1px solid rgba(248,113,113,0.4)",
  background: "rgba(248,113,113,0.14)",
  color: "#FCA5A5",
  fontSize: 13,
  fontWeight: 900,
  cursor: "pointer"
};

function CenteredMessage({ children }) {
  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 480, margin: "18vh auto 0", textAlign: "center" }}>
        <p style={{ margin: 0, color: "#c7ced9", fontSize: 14, lineHeight: 1.6 }}>{children}</p>
        <Link to={ADMIN_PATH} style={{ ...backLinkStyle, marginTop: 18, justifyContent: "center" }}>
          <ArrowLeft size={14} /> Voltar
        </Link>
      </div>
    </div>
  );
}

export default function AdminUsuarios() {
  // "checking" -> confirmando sessão/papel com o servidor antes de mostrar
  // qualquer coisa; "forbidden" -> não é admin (ou nem está logado);
  // "ready" -> pode ver e editar a lista.
  const [status, setStatus] = useState("checking");
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [feedback, setFeedback] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [viewerId, setViewerId] = useState(null);
  const [roleFilter, setRoleFilter] = useState([]); // OR: mostra quem tem QUALQUER um dos papeis marcados aqui

  // Modal "Editar dados"
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", whatsapp: "", state: "", city: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  // Modal "Redefinir senha" -- nunca gera nem mostra senha em texto puro, so
  // um link de uso unico (mesmo mecanismo do "esqueci minha senha").
  const [resetTargetUser, setResetTargetUser] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [resetError, setResetError] = useState("");

  // Modal de confirmacao de exclusao -- substitui o antigo window.confirm
  // (dialogo nativo, sem estilo, e que trava outras interacoes da pagina).
  const [deleteTargetUser, setDeleteTargetUser] = useState(null);

  useEffect(() => {
    let alive = true;
    apiFetch("/auth/me", { method: "GET", skipSessionExpiredHandler: true })
      .then((me) => {
        if (!alive) return;
        if (getEffectiveRoles(me).includes("ADMIN") && !me.passwordMustChange) {
          setViewerId(me.id);
          setStatus("ready");
        } else {
          setStatus("forbidden");
        }
      })
      .catch(() => {
        if (alive) setStatus("forbidden");
      });
    return () => {
      alive = false;
    };
  }, []);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    setFeedback("");
    try {
      const data = await apiFetch("/admin/users", { method: "GET" });
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      setFeedback("Não foi possível carregar a lista de usuários agora.");
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (status === "ready") loadUsers();
  }, [status, loadUsers]);

  const saveRoles = async (userId, nextRoles) => {
    const previousUsers = users;
    setFeedback("");
    setSavingId(userId);
    // Atualização otimista -- os checkboxes refletem a escolha na hora; se o
    // servidor recusar, a lista é recarregada e volta pro estado real.
    setUsers((prev) => prev.map((item) => (item.id === userId ? { ...item, roles: nextRoles } : item)));
    try {
      const updated = await apiPatch(`/users/${encodeURIComponent(userId)}/roles`, { body: { roles: nextRoles } });
      setUsers((prev) => prev.map((item) => (item.id === userId ? updated : item)));
    } catch (error) {
      setUsers(previousUsers);
      setFeedback(error?.message || "Não foi possível salvar os papéis dessa pessoa.");
    } finally {
      setSavingId(null);
    }
  };

  const toggleRole = (user, role) => {
    // Autoedicao dos proprios papeis extras e' permitida (decisao do Drigo,
    // 29/10/2026) -- so' o proprio papel de Admin continua travado por aqui,
    // pra ninguem perder o proprio acesso por engano (mesma trava reforcada
    // no backend, users.js).
    if (user.id === viewerId && role === "ADMIN") return;
    const hasRole = user.roles?.includes(role);
    const nextRoles = hasRole ? user.roles.filter((item) => item !== role) : [...(user.roles || []), role];
    if (nextRoles.length === 0) {
      setFeedback("Cada pessoa precisa de pelo menos um papel -- marque outro antes de desmarcar este.");
      return;
    }
    saveRoles(user.id, nextRoles);
  };

  // Filtro por papel (independente da busca por nome/e-mail) -- clique de
  // novo no mesmo papel pra remover do filtro. OR entre os papeis marcados:
  // "Admin" + "Pastor" mostra quem tem qualquer um dos dois.
  const toggleRoleFilter = (role) => {
    setRoleFilter((prev) => (prev.includes(role) ? prev.filter((item) => item !== role) : [...prev, role]));
  };

  const requestDeleteUser = (user) => {
    if (user.id === viewerId) return; // não exclui a própria conta por aqui
    setDeleteTargetUser(user);
  };

  const confirmDeleteUser = async () => {
    const user = deleteTargetUser;
    if (!user) return;
    setFeedback("");
    setDeletingId(user.id);
    try {
      await apiDelete(`/users/${encodeURIComponent(user.id)}`);
      setUsers((prev) => prev.filter((item) => item.id !== user.id));
      setDeleteTargetUser(null);
    } catch (error) {
      setFeedback(error?.message || "Não foi possível excluir essa pessoa.");
      setDeleteTargetUser(null);
    } finally {
      setDeletingId(null);
    }
  };

  const openEditModal = (user) => {
    setEditError("");
    setEditForm({
      name: user.name || "",
      email: user.email || "",
      whatsapp: user.whatsapp || "",
      state: user.state || "",
      city: user.city || ""
    });
    setEditingUser(user);
  };

  const closeEditModal = () => {
    if (editSaving) return;
    setEditingUser(null);
    setEditError("");
  };

  const saveEditModal = async () => {
    if (!editingUser) return;
    setEditSaving(true);
    setEditError("");
    try {
      const updated = await apiPatch(`/users/${encodeURIComponent(editingUser.id)}/admin-fields`, {
        body: {
          name: editForm.name.trim(),
          email: editForm.email.trim(),
          whatsapp: editForm.whatsapp.trim() || null,
          state: editForm.state.trim() || null,
          city: editForm.city.trim() || null
        }
      });
      setUsers((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setEditingUser(null);
    } catch (error) {
      setEditError(error?.message || "Não foi possível salvar os dados dessa pessoa.");
    } finally {
      setEditSaving(false);
    }
  };

  const openResetModal = (user) => {
    setResetResult(null);
    setResetError("");
    setResetTargetUser(user);
  };

  const closeResetModal = () => {
    if (resetLoading) return;
    setResetTargetUser(null);
    setResetResult(null);
    setResetError("");
  };

  const sendResetLink = async () => {
    if (!resetTargetUser) return;
    setResetLoading(true);
    setResetError("");
    try {
      const result = await apiPost(`/users/${encodeURIComponent(resetTargetUser.id)}/request-password-reset`);
      setResetResult(result);
    } catch (error) {
      setResetError(error?.message || "Não foi possível gerar o link de redefinição.");
    } finally {
      setResetLoading(false);
    }
  };

  const normalize = (value) => (value || "").toLowerCase();
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      !search.trim() || normalize(user.name).includes(normalize(search)) || normalize(user.email).includes(normalize(search));
    const matchesRoleFilter = roleFilter.length === 0 || roleFilter.some((role) => user.roles?.includes(role));
    return matchesSearch && matchesRoleFilter;
  });
  const sortedFilteredUsers = [...filteredUsers].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "", "pt-BR", { sensitivity: "base" })
  );
  const hasActiveFilter = Boolean(search.trim()) || roleFilter.length > 0;

  if (status === "checking") {
    return <CenteredMessage>Carregando...</CenteredMessage>;
  }

  if (status === "forbidden") {
    return <CenteredMessage>Esta página é restrita a administradores. Entre com uma conta ADMIN para ver e gerenciar os usuários.</CenteredMessage>;
  }

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <Link to={ADMIN_PATH} style={backLinkStyle}>
          <ArrowLeft size={14} /> Voltar
        </Link>

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
          <div>
            <p style={{ margin: "0 0 6px", fontSize: 11, letterSpacing: 1.35, color: "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>Gestão de usuários</p>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 950, color: "#FFF" }}>Quem tem acesso a quê</h1>
          </div>
          <button
            onClick={loadUsers}
            disabled={usersLoading}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 38, padding: "0 14px", border: "1px solid rgba(125,211,252,0.38)", background: "rgba(14,165,233,0.12)", color: "#BAE6FD", fontSize: 12, fontWeight: 900, borderRadius: 8, cursor: usersLoading ? "default" : "pointer", opacity: usersLoading ? 0.7 : 1 }}
          >
            <RefreshCw size={13} />
            {usersLoading ? "Atualizando..." : "Atualizar"}
          </button>
        </div>
        <p style={{ margin: "0 0 22px", color: "#9fb0cc", fontSize: 13, lineHeight: 1.6, maxWidth: 620 }}>
          Marque quantos papéis cada pessoa precisar -- por exemplo, ACOLHIMENTO e FINANCEIRO juntos, sem precisar virar ADMIN. ADMIN sempre tem acesso a tudo, independente dos outros papéis marcados.
        </p>

        <div style={{ position: "relative", maxWidth: 320, marginBottom: 12 }}>
          <Search size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#6b7d99", pointerEvents: "none" }} />
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por nome ou e-mail"
            style={{ ...inputStyle, paddingLeft: 34 }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 800, color: "#7f8ba3", textTransform: "uppercase", letterSpacing: 0.6 }}>Filtrar por papel:</span>
          {ROLE_OPTIONS.map((role) => {
            const active = roleFilter.includes(role);
            return (
              <button
                key={role}
                type="button"
                onClick={() => toggleRoleFilter(role)}
                aria-pressed={active}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "5px 10px",
                  borderRadius: 999,
                  border: active ? "1px solid rgba(125,211,252,0.55)" : "1px solid rgba(148,163,184,0.24)",
                  background: active ? "rgba(14,165,233,0.16)" : "rgba(255,255,255,0.03)",
                  color: active ? "#BAE6FD" : "#9fb0cc",
                  fontSize: 11,
                  fontWeight: 800,
                  cursor: "pointer"
                }}
              >
                {ROLE_LABELS[role]}
              </button>
            );
          })}
          {roleFilter.length > 0 && (
            <button
              type="button"
              onClick={() => setRoleFilter([])}
              style={{ border: "none", background: "none", color: "#7DD3FC", fontSize: 11, fontWeight: 800, cursor: "pointer", padding: "5px 4px" }}
            >
              Limpar
            </button>
          )}
        </div>

        {hasActiveFilter && (
          <p style={{ margin: "0 0 16px", color: "#7f8ba3", fontSize: 12, fontWeight: 700 }}>
            {filteredUsers.length} de {users.length} {users.length === 1 ? "usuário" : "usuários"}
          </p>
        )}

        {feedback && (
          <p style={{ margin: "0 0 16px", color: "#FCA5A5", fontSize: 13, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.28)", borderRadius: 10, padding: "10px 12px" }}>
            {feedback}
          </p>
        )}

        <div style={cardStyle}>
          {users.length === 0 && !usersLoading && (
            <p style={{ margin: 0, color: "#9fb0cc", fontSize: 13 }}>Nenhum usuário carregado.</p>
          )}
          {users.length > 0 && filteredUsers.length === 0 && (
            <p style={{ margin: 0, color: "#9fb0cc", fontSize: 13 }}>
              Nenhum usuário encontrado{search.trim() ? ` para "${search}"` : ""}
              {roleFilter.length > 0 ? " com esse(s) papel(éis)" : ""}.
            </p>
          )}

          {filteredUsers.length > 0 && (
            // Tabela de verdade em vez de repetir os 7 rotulos de papel em
            // toda linha (o que tinha antes) -- os nomes de papel viram
            // cabecalho de coluna, mostrados uma unica vez, e cada linha so
            // guarda o estado (marcado/desmarcado). Reduz bastante o texto
            // repetido na tela e deixa "escanear uma coluna" (quem tem
            // Financeiro, por exemplo) genuinamente mais rapido. Em telas
            // estreitas a tabela rola horizontalmente (overflowX) em vez de
            // quebrar o layout.
            //
            // overflowY:"hidden" (em vez de deixar so overflowX) e de
            // proposito: CSS converte overflow-y "visible" pra "auto"
            // sozinho quando overflow-x nao e "visible" (regra do spec), e
            // isso fazia esse div virar container de scroll vertical PROPRIO
            // -- com altura igual ao conteudo, sem nunca rolar de verdade,
            // mas o suficiente pra "position: sticky" do cabecalho colar
            // nele em vez de colar na janela. Resultado: rolava a pagina e o
            // cabecalho ia embora junto (reportado pelo Rodrigo com
            // screenshot de /admin/usuarios). "hidden" fecha essa brecha sem
            // cortar nada, porque a altura do div nunca fica menor que o
            // conteudo.
            //
            // CORRECAO (10/2026, reportado de novo por Rodrigo com
            // screenshot: cabecalho ainda sumia rolando): o raciocinio acima
            // tava incompleto. overflow-y:hidden, mesmo nunca chegando a
            // cortar nada de verdade, AINDA conta pro spec como "este div
            // estabelece uma caixa de rolagem" -- e e' exatamente essa caixa
            // (nao o <html>) que position:sticky usa como referencia pra
            // decidir quando "grudar". Como esse div em si nunca rola de
            // verdade (a propria altura dele e' sempre igual ao conteudo),
            // o sticky fica "escutando" um scroll que nunca acontece, e o
            // cabecalho so' anda junto com o resto da pagina, estatico --
            // igual nao tivesse sticky nenhum. overflow-y:"clip" resolve as
            // duas pontas ao mesmo tempo: corta overflow vertical sem
            // precisar de scrollbar (mesmo efeito pratico do hidden aqui) E,
            // ao contrario de hidden/auto, NAO conta como caixa de rolagem
            // pro spec -- o sticky atravessa esse div e enxerga o <html> (o
            // scroll real) do outro lado, que e' o que faz ele de fato
            // grudar. Suporte a overflow:clip e' universal nos navegadores
            // modernos ha vários anos; sem ele, cai de volta pro
            // comportamento antigo (auto nos dois eixos), nao pro bug do
            // cabeçalho sumindo.
            //
            // CORRECAO 2 (10/2026, reportado de novo por Rodrigo com
            // screenshot -- cabecalho ainda sumia MESMO depois do
            // overflow:clip acima): faltava uma segunda causa, independente
            // da primeira. Com borderCollapse:"collapse" (o que a tabela
            // usava), o Chrome simplesmente ignora position:sticky em
            // <th>/<thead> -- bug antigo e conhecido do motor de tabelas
            // (sticky so' funciona de verdade no modelo de bordas
            // "separate"). As duas causas coexistiam e cada uma sozinha ja'
            // bastava pra quebrar o sticky; corrigir so' uma (como da
            // primeira vez) nao resolvia. Trocado pra borderCollapse:
            // "separate" + borderSpacing:0 (sem espaco visivel entre
            // celulas, visual identico). Unico efeito colateral: no modelo
            // "separate" uma <tr> nao pode ter borda propria (so' table/td/
            // th podem) -- por isso a borda de cima de cada linha, que
            // estava no style da <tr> logo abaixo, foi movida pro style de
            // cada <td> daquela linha (mesma cor/espessura, so' mudou de
            // elemento).
            //
            // CORRECAO 3 (10/2026, reportado de novo por Rodrigo com
            // screenshot -- cabecalho AINDA sumia depois das correcoes 1 e
            // 2): o raciocinio da correcao 1 em si tinha um furo. overflow-
            // y:"clip" realmente evita que o EIXO Y deste div vire caixa de
            // rolagem -- mas overflowX:"auto" sozinho ja' e' suficiente pra
            // o div inteiro contar como "caixa de rolagem" pro spec (basta
            // UM eixo nao-visible/nao-clip pra isso, nao precisa ser o eixo
            // Y). E a escolha de "qual e' a caixa de rolagem mais proxima"
            // que position:sticky usa como referencia NAO e' por eixo: e'
            // por elemento. Resultado pratico: mesmo com overflow-y:clip,
            // esse div com overflowX:"auto" continuava sendo escolhido como
            // referencia do sticky em vez do <html> -- e como ele nunca
            // tem scroll vertical de verdade (a altura dele sempre cresce
            // igual ao conteudo), o cabecalho "grudava" numa rolagem que
            // nunca acontece, e so' andava junto com a pagina, como se nao
            // tivesse sticky nenhum. As correcoes 1 e 2 eram reais e
            // continuam necessarias (ambas evitam outras causas de quebra),
            // mas nenhuma das duas resolvia ESTA, porque nenhuma mexia em
            // quem e' a caixa de rolagem.
            //
            // A saida de verdade pra "tabela larga que precisa rolar na
            // horizontal" + "cabecalho que gruda ao rolar na vertical" e'
            // aceitar que ESTE div tem mesmo que ser a caixa de rolagem (pro
            // sticky ter uma rolagem real pra "escutar") -- so' que rolando
            // nos dois eixos dentro de uma altura limitada, em vez de tentar
            // fugir disso. Por isso: altura maxima (maxHeight) relativa a'
            // tela + overflowY:"auto" (rolagem vertical de verdade dentro
            // do proprio cartao da lista, com barra de rolagem propria),
            // no lugar de contar com o scroll da pagina inteira. Mesmo
            // padrao usado por qualquer grade de dados com cabecalho fixo
            // (ex. Google Sheets, planilhas web) -- painel com altura
            // definida, rolagem interna, cabecalho grudado de verdade.
            <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "68vh" }}>
              <table style={{ width: "100%", minWidth: 1010, borderCollapse: "separate", borderSpacing: 0 }}>
                <thead>
                  <tr>
                    <th style={{ position: "sticky", top: 0, zIndex: 2, background: "#0f1727", textAlign: "left", minWidth: 220, padding: "0 10px 10px 0", fontSize: 11, fontWeight: 800, color: "#7f8ba3", textTransform: "uppercase", letterSpacing: 0.4 }}>
                      Pessoa
                    </th>
                    {ROLE_OPTIONS.map((role) => (
                      <th
                        key={role}
                        title={ROLE_LABELS[role]}
                        style={{ position: "sticky", top: 0, zIndex: 2, background: "#0f1727", width: 74, padding: "0 4px 10px", fontSize: 10, fontWeight: 800, color: "#7f8ba3", textAlign: "center", lineHeight: 1.25 }}
                      >
                        {ROLE_LABELS[role]}
                      </th>
                    ))}
                    <th style={{ position: "sticky", top: 0, zIndex: 2, background: "#0f1727", width: 118 }} />
                  </tr>
                </thead>
                <tbody>
                  {sortedFilteredUsers.map((user) => {
                    const isSelf = user.id === viewerId;
                    return (
                      <tr
                        key={user.id}
                        style={{
                          opacity: savingId === user.id || deletingId === user.id ? 0.6 : 1
                        }}
                      >
                        <td style={{ padding: "12px 10px 12px 0", minWidth: 220, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                          <p style={{ margin: 0, color: "#E5E7EB", fontSize: 14, fontWeight: 850 }}>
                            {user.name} {isSelf && <span style={{ color: "#7DD3FC", fontWeight: 700 }}>(você)</span>}
                          </p>
                          <p style={{ margin: "3px 0 0", color: "#9fb0cc", fontSize: 12, overflowWrap: "anywhere" }}>{user.email || "sem e-mail"}</p>
                          {(() => {
                            // Vinculo de membro: caminho antigo (linkedMember) OU via
                            // Person (ver model Person no schema) -- cobre quem converteu
                            // de kids/teens pra membro e ficou linkado so pela Person.
                            const linkedMemberRecord = user.linkedMember || user.person?.member;
                            // Vinculo de kids/teens (ex.: Bernardo) -- so existe via
                            // Person, Child nunca teve coluna linkedUserId propria.
                            const linkedChildRecord = user.person?.child;

                            if (linkedMemberRecord) return null;

                            if (linkedChildRecord) {
                              // Tem cadastro, so' que no ministerio infantil (kids/teens)
                              // em vez de membresia -- evita o falso "sem cadastro" pra
                              // quem e' teen e ja tem conta propria.
                              return (
                                <span style={{ display: "inline-flex", marginTop: 5, padding: "2px 8px", borderRadius: 999, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.2, color: "#7DD3FC", background: "rgba(56,189,248,0.14)", border: "1px solid rgba(56,189,248,0.35)" }}>
                                  Conta do app · vinculada a kids/teens ({linkedChildRecord.fullName})
                                </span>
                              );
                            }

                            // Conta de app sem cadastro de membro (nem de kids/teens)
                            // vinculado -- o papel que essa conta tem marcado abaixo
                            // (VISITANTE desde 10/2026, ou ainda MEMBER em conta mais
                            // antiga que nao foi migrada por algum motivo) e' so' o
                            // acesso basico que toda conta nova ganha (ver roles:
                            // ["VISITANTE"] em auth.js), nao significa que a pessoa e'
                            // membro oficial da igreja. Mesmo selo usado em
                            // /admin/acolhimento pra esse mesmo caso.
                            return (
                              <span style={{ display: "inline-flex", marginTop: 5, padding: "2px 8px", borderRadius: 999, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.2, color: "#7DD3FC", background: "rgba(56,189,248,0.14)", border: "1px solid rgba(56,189,248,0.35)" }}>
                                Conta do app · sem cadastro de membro
                              </span>
                            );
                          })()}
                        </td>
                        {ROLE_OPTIONS.map((role) => {
                          const checked = Boolean(user.roles?.includes(role));
                          // So' o checkbox de Admin da propria linha fica travado --
                          // os demais papeis agora podem ser autoeditados (ver toggleRole).
                          const adminLockedForSelf = isSelf && role === "ADMIN";
                          return (
                            <td key={role} style={{ textAlign: "center", padding: "12px 4px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                              <label
                                title={adminLockedForSelf ? "Não é possível remover o próprio papel de Admin por aqui" : ROLE_LABELS[role]}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 30,
                                  height: 30,
                                  borderRadius: 8,
                                  border: checked ? "1px solid rgba(125,211,252,0.55)" : "1px solid rgba(148,163,184,0.24)",
                                  background: checked ? "rgba(14,165,233,0.16)" : "rgba(255,255,255,0.03)",
                                  cursor: adminLockedForSelf ? "not-allowed" : "pointer"
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={adminLockedForSelf || savingId === user.id}
                                  onChange={() => toggleRole(user, role)}
                                  style={{ margin: 0 }}
                                  aria-label={`${ROLE_LABELS[role]} -- ${user.name}`}
                                />
                              </label>
                            </td>
                          );
                        })}
                        <td style={{ padding: "12px 0 12px 4px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                            <button
                              type="button"
                              onClick={() => openEditModal(user)}
                              disabled={savingId === user.id || deletingId === user.id}
                              title="Editar dados"
                              style={iconButtonStyle}
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              type="button"
                              onClick={() => openResetModal(user)}
                              disabled={savingId === user.id || deletingId === user.id}
                              title="Redefinir senha"
                              style={iconButtonStyle}
                            >
                              <KeyRound size={15} />
                            </button>
                            {/* Separador visual antes do excluir -- essa e' a unica acao
                                destrutiva do grupo (as outras duas sao reversiveis), e
                                antes ficava colada na chave de redefinir senha sem
                                nenhuma distincao, facil de clicar sem querer (ha um
                                modal de confirmacao depois, mas o primeiro clique nao
                                devia ja parecer identico aos outros dois). */}
                            <span style={{ width: 1, height: 18, background: "rgba(148,163,184,0.22)", margin: "0 2px" }} />
                            <button
                              type="button"
                              onClick={() => requestDeleteUser(user)}
                              disabled={isSelf || savingId === user.id || deletingId === user.id}
                              title={isSelf ? "Não é possível excluir a própria conta por aqui" : "Excluir usuário"}
                              style={{
                                ...iconButtonStyle,
                                color: isSelf ? "#3f4b5e" : "#8a96ab",
                                cursor: isSelf ? "not-allowed" : "pointer"
                              }}
                              onMouseEnter={(event) => {
                                if (!isSelf) event.currentTarget.style.color = "#FCA5A5";
                              }}
                              onMouseLeave={(event) => {
                                if (!isSelf) event.currentTarget.style.color = "#8a96ab";
                              }}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editingUser && (
        <div style={modalBackdropStyle} onClick={closeEditModal}>
          <div style={{ ...modalCardStyle, position: "relative" }} onClick={(event) => event.stopPropagation()}>
            <button type="button" onClick={closeEditModal} style={modalCloseButtonStyle} disabled={editSaving}>
              <X size={16} />
            </button>
            <p style={{ margin: "0 0 4px", fontSize: 11, letterSpacing: 1.2, color: "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>Editar dados</p>
            <h2 style={{ margin: "0 0 16px", fontSize: 19, fontWeight: 900, color: "#FFF" }}>{editingUser.name}</h2>

            {editError && (
              <p style={{ margin: "0 0 14px", color: "#FCA5A5", fontSize: 13, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.28)", borderRadius: 10, padding: "10px 12px" }}>
                {editError}
              </p>
            )}

            <div style={{ display: "grid", gap: 14 }}>
              <div>
                <label style={fieldLabelStyle}>Nome</label>
                <input type="text" value={editForm.name} onChange={(event) => setEditForm((prev) => ({ ...prev, name: event.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={fieldLabelStyle}>E-mail</label>
                <input type="email" value={editForm.email} onChange={(event) => setEditForm((prev) => ({ ...prev, email: event.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={fieldLabelStyle}>WhatsApp</label>
                <input type="text" value={editForm.whatsapp} onChange={(event) => setEditForm((prev) => ({ ...prev, whatsapp: event.target.value }))} style={inputStyle} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={fieldLabelStyle}>Estado</label>
                  <input type="text" value={editForm.state} onChange={(event) => setEditForm((prev) => ({ ...prev, state: event.target.value }))} style={inputStyle} />
                </div>
                <div>
                  <label style={fieldLabelStyle}>Cidade</label>
                  <input type="text" value={editForm.city} onChange={(event) => setEditForm((prev) => ({ ...prev, city: event.target.value }))} style={inputStyle} />
                </div>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
              <button type="button" onClick={closeEditModal} disabled={editSaving} style={secondaryButtonStyle}>
                Cancelar
              </button>
              <button type="button" onClick={saveEditModal} disabled={editSaving} style={{ ...primaryButtonStyle, opacity: editSaving ? 0.7 : 1 }}>
                {editSaving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetTargetUser && (
        <div style={modalBackdropStyle} onClick={closeResetModal}>
          <div style={{ ...modalCardStyle, position: "relative" }} onClick={(event) => event.stopPropagation()}>
            <button type="button" onClick={closeResetModal} style={modalCloseButtonStyle} disabled={resetLoading}>
              <X size={16} />
            </button>
            <p style={{ margin: "0 0 4px", fontSize: 11, letterSpacing: 1.2, color: "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>Redefinir senha</p>
            <h2 style={{ margin: "0 0 16px", fontSize: 19, fontWeight: 900, color: "#FFF" }}>{resetTargetUser.name}</h2>

            {resetError && (
              <p style={{ margin: "0 0 14px", color: "#FCA5A5", fontSize: 13, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.28)", borderRadius: 10, padding: "10px 12px" }}>
                {resetError}
              </p>
            )}

            {!resetResult ? (
              <>
                <p style={{ margin: "0 0 20px", color: "#9fb0cc", fontSize: 13, lineHeight: 1.6 }}>
                  Vamos gerar um link de redefinição de senha, válido por 30 minutos, para{" "}
                  <strong style={{ color: "#e5e7eb" }}>{resetTargetUser.email || "esta pessoa"}</strong>. Ninguém vê a senha atual nem uma senha temporária -- a própria pessoa escolhe a nova senha ao abrir o link.
                </p>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                  <button type="button" onClick={closeResetModal} disabled={resetLoading} style={secondaryButtonStyle}>
                    Cancelar
                  </button>
                  <button type="button" onClick={sendResetLink} disabled={resetLoading} style={{ ...primaryButtonStyle, opacity: resetLoading ? 0.7 : 1 }}>
                    {resetLoading ? "Gerando..." : "Gerar link"}
                  </button>
                </div>
              </>
            ) : resetResult.emailDeliveryConfigured ? (
              <>
                <p style={{ margin: "0 0 20px", color: "#9fb0cc", fontSize: 13, lineHeight: 1.6 }}>
                  Link enviado por e-mail para <strong style={{ color: "#e5e7eb" }}>{resetTargetUser.email}</strong>. Ele vale por 30 minutos.
                </p>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button type="button" onClick={closeResetModal} style={primaryButtonStyle}>
                    Fechar
                  </button>
                </div>
              </>
            ) : (
              <>
                <p style={{ margin: "0 0 10px", color: "#9fb0cc", fontSize: 13, lineHeight: 1.6 }}>
                  O envio automático de e-mail não está configurado agora -- copie o link abaixo e envie manualmente para esta pessoa. Ele vale por 30 minutos.
                </p>
                <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
                  <input type="text" readOnly value={resetResult.resetUrl} style={{ ...inputStyle, fontSize: 12 }} onFocus={(event) => event.target.select()} />
                  <button type="button" onClick={() => navigator.clipboard?.writeText(resetResult.resetUrl)} style={{ ...secondaryButtonStyle, flex: "none" }}>
                    Copiar
                  </button>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button type="button" onClick={closeResetModal} style={primaryButtonStyle}>
                    Fechar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {deleteTargetUser && (
        <div style={modalBackdropStyle} onClick={() => !deletingId && setDeleteTargetUser(null)}>
          <div style={{ ...modalCardStyle, position: "relative" }} onClick={(event) => event.stopPropagation()}>
            <button type="button" onClick={() => setDeleteTargetUser(null)} style={modalCloseButtonStyle} disabled={Boolean(deletingId)}>
              <X size={16} />
            </button>
            <p style={{ margin: "0 0 4px", fontSize: 11, letterSpacing: 1.2, color: "#FCA5A5", textTransform: "uppercase", fontWeight: 900 }}>Excluir usuário</p>
            <h2 style={{ margin: "0 0 16px", fontSize: 19, fontWeight: 900, color: "#FFF" }}>{deleteTargetUser.name}</h2>
            <p style={{ margin: "0 0 22px", color: "#9fb0cc", fontSize: 13, lineHeight: 1.6 }}>
              Excluir definitivamente esta conta? Essa ação não pode ser desfeita -- remove a conta, o progresso nas trilhas e os grupos que essa pessoa possui.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button type="button" onClick={() => setDeleteTargetUser(null)} disabled={Boolean(deletingId)} style={secondaryButtonStyle}>
                Cancelar
              </button>
              <button type="button" onClick={confirmDeleteUser} disabled={Boolean(deletingId)} style={{ ...dangerButtonStyle, opacity: deletingId ? 0.7 : 1 }}>
                {deletingId ? "Excluindo..." : "Excluir definitivamente"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
